import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { withRouter } from 'react-router-dom';
import css from './GetStartedPage2.css';
import config from '../../config';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import { parseLinkedPlan } from './planCatalog';

// Same public, no-auth marketing page as before. Subscribing now navigates
// to the full-page checkout at /checkout-plan/:tier (see
// src/containers/CheckoutPlanPage/CheckoutPlanPage.js) instead of opening
// an on-page popup — that page carries the same Chargebee Payment
// Components integration this file used to host directly.
function GetStartedPage2(props) {
  const linkedPlan =
    typeof window !== 'undefined' ? parseLinkedPlan(window.location.search) : null;

  const [period, setPeriod] = useState(linkedPlan ? linkedPlan.period : 'monthly');
  const [lifetime, setLifetime] = useState({ seatsRemaining: null, soldOut: false });

  const highlightedTier = linkedPlan && linkedPlan.tier;
  const networkCardRef = useRef(null);
  const verifiedCardRef = useRef(null);
  const lifetimeCardRef = useRef(null);

  // Scroll straight to the linked plan's card so a visitor arriving from a
  // direct marketing link (e.g. /get-started2?plan=verified-monthly) sees
  // exactly what they clicked for, without hunting through all three.
  useEffect(() => {
    if (!highlightedTier) return;
    const ref =
      highlightedTier === 'network'
        ? networkCardRef
        : highlightedTier === 'verified'
        ? verifiedCardRef
        : lifetimeCardRef;
    if (ref.current && ref.current.scrollIntoView) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    axios
      .post(`${config.subscriptionServiceUrl}eligible-plans`, {})
      .then(res => {
        const data = res.data || {};
        const seatsRemaining = data.lifetime && data.lifetime.seatsRemaining;
        setLifetime({
          seatsRemaining: typeof seatsRemaining === 'number' ? seatsRemaining : null,
          soldOut: seatsRemaining === 0,
        });
      })
      .catch(() => {
        // Best-effort — if this fails we just show the Lifetime card
        // without a seat count rather than blocking the whole page.
      });
  }, []);

  const isAnnual = period === 'annual';

  const goToCheckout = (tier, checkoutPeriod) => {
    const query = checkoutPeriod ? `?period=${checkoutPeriod}` : '';
    props.history.push(`/checkout-plan/${tier}${query}`);
  };

  // Go straight to checkout when arriving via a direct plan link, same as
  // the popup used to auto-open in that case. replace() (not push()) so
  // the back button returns to wherever the visitor came from, not this
  // redirect step.
  useEffect(() => {
    if (!linkedPlan) return;
    props.history.replace(`/checkout-plan/${linkedPlan.tier}?period=${linkedPlan.period}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageWrapper>
      <div className={css.base}>
        <div className={css.hero}>
          <span className={css.brand}>
            Viveka<span className={css.brandDot}>.</span>
          </span>
          <h1 className={css.title}>Join the Viveka Coach Network</h1>
          <p className={css.subtitle}>
            Pick a plan and pay securely &mdash; your coach account is ready the moment payment
            completes, no separate signup step.
          </p>

          <div className={css.toggleRow}>
            <span className={`${css.toggleLabel} ${!isAnnual ? css.toggleLabelActive : ''}`}>
              Monthly
            </span>
            <button
              type="button"
              className={`${css.toggleSwitch} ${isAnnual ? css.toggleSwitchOn : ''}`}
              onClick={() => setPeriod(isAnnual ? 'monthly' : 'annual')}
              aria-label="Toggle annual billing"
            >
              <span className={`${css.toggleKnob} ${isAnnual ? css.toggleKnobOn : ''}`} />
            </button>
            <span className={`${css.toggleLabel} ${isAnnual ? css.toggleLabelActive : ''}`}>
              Annual
            </span>
            <span className={css.saveBadge}>SAVE 15%+</span>
          </div>
        </div>

        <div className={css.grid}>
          <div
            ref={networkCardRef}
            className={`${css.card} ${highlightedTier === 'network' ? css.cardHighlighted : ''}`}
          >
            <span className={`${css.badge} ${css.badgeNetwork}`}>Network Member</span>
            <h3 className={css.cardTitle}>Viveka Network Member</h3>
            <div className={css.price}>
              {isAnnual ? '$197' : '$19'}
              <span className={css.priceUnit}>{isAnnual ? '/year' : '/month'}</span>
            </div>
            {isAnnual && <div className={css.savingsNote}>Save $31/year vs monthly</div>}
            <ul className={css.list}>
              <li>Professional coaching profile in the Viveka marketplace</li>
              <li>Practice management tools &mdash; scheduling, accountability, programs, ads</li>
              <li>Monthly coaching industry insights</li>
            </ul>
            <button
              type="button"
              className={`${css.ctaBtn} ${css.ctaSecondary}`}
              onClick={() => goToCheckout('network', period)}
            >
              {isAnnual ? 'Subscribe' : 'Start 7-Day Free Trial'}
            </button>
          </div>

          <div
            ref={lifetimeCardRef}
            className={`${css.card} ${css.cardLifetime} ${
              highlightedTier === 'lifetime' ? css.cardHighlighted : ''
            }`}
          >
            <span className={`${css.badge} ${css.badgeLifetime}`}>Viveka First 100</span>
            <h3 className={css.cardTitle}>Verified Coach &mdash; Lifetime</h3>
            <div className={css.price}>
              $997<span className={css.priceUnit}> one-time</span>
            </div>
            <ul className={css.list}>
              <li>Lifetime Verified Coach status &mdash; locked permanently</li>
              <li>Priority visibility across the network</li>
              <li>No renewals. No future price increases.</li>
            </ul>
            {lifetime.seatsRemaining !== null && (
              <div className={css.seatNote}>{lifetime.seatsRemaining} of 100 seats remaining</div>
            )}
            {lifetime.soldOut ? (
              <div className={css.soldOut}>Sold out</div>
            ) : (
              <button
                type="button"
                className={`${css.ctaBtn} ${css.ctaGold}`}
                onClick={() => goToCheckout('lifetime')}
              >
                Lock In Lifetime Access
              </button>
            )}
          </div>

          <div
            ref={verifiedCardRef}
            className={`${css.card} ${css.cardFeatured} ${
              highlightedTier === 'verified' ? css.cardHighlighted : ''
            }`}
          >
            <span className={`${css.badge} ${css.badgeVerified}`}>Verified Coach</span>
            <h3 className={css.cardTitle}>Viveka Verified Coach</h3>
            <div className={css.price}>
              {isAnnual ? '$497' : '$49'}
              <span className={css.priceUnit}>{isAnnual ? '/year' : '/month'}</span>
            </div>
            {isAnnual && <div className={css.savingsNote}>Save $91/year vs monthly</div>}
            <ul className={css.list}>
              <li>Viveka Verification badge with priority placement on Marketplace Ads</li>
              <li>Eligibility for enterprise engagements and Viveka spotlight features</li>
              <li>Reporting and analytics, file uploads, and 10 active listings</li>
            </ul>
            <button
              type="button"
              className={`${css.ctaBtn} ${css.ctaPrimary}`}
              onClick={() => goToCheckout('verified', period)}
            >
              {isAnnual ? 'Subscribe' : 'Start 7-Day Free Trial'}
            </button>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}

export default withRouter(GetStartedPage2);
